import Client from "@framelink/flclient";
import Msg from "./message";
import { arrayBufferToBase64, base64ToArrayBuffer, escapePostgre, log } from "../utils";
import IServiceSms from "../interface/service-sms";
import IServiceGroupKey from "../interface/service-group-key";
import IServiceEmail from "../interface/service-email";
import IParamItem from "../interface/param-item";
import ILinkedFieldClientConfig from "../interface/field-cfg-client-linked";
import IField from "../interface/field";
import IDocument from "../interface/document";
import ICallUser from "../interface/call-user";
import IApiWebhhokDico from "../interface/api/api-wenhook-dico";
import IApiWebhookStatus from "../interface/api/api-webhook-status";
import IApiWebhookInfo from "../interface/api/api-webhook-info";
import IApiUserLoaded from "../interface/api/api-user-loaded";
import IApiRollupData from "../interface/api/api-rollup-data";
import IApiRollup from "../interface/api/api-rollup";
import IApiResponseFieldToString from "../interface/api/api-response-field-to-string";
import IApiResponseCreateApp from "../interface/api/api-response-create-app";
import IApiParamFactory from "../interface/api/api-param-factory";
import IApiParamEntity from "../interface/api/api-param-entity";
import IApiMasterDetail from "../interface/api/api-master-detail";
import IApiLoginData from "../interface/api/api-login-data";
import IApiLoginApplication from "../interface/api/api-login-application";
import IApiLinkedFieldConfig from "../interface/api/api-linked-field-config";
import IApiGroupKey from "../interface/api/api-group-key";
import IApiFileResponse from "../interface/api/api-file-response";
import IApiField from "../interface/api/api-field";
import IApiEntity from "../interface/api/api-entity";
import IApiChangeToLinked from "../interface/api/api-change-to-linked";
import IApiArgsGrantAccess from "../interface/api/api-args-grant-access";
import IApiApplication from "../interface/api/api-application";
import Global from "../global";
import ServiceName from "../enum/service-name";
import ServiceApiName from "../enum/service-api-name";
import ServerErrorCode from "../enum/server-error-code";
import Message from "../enum/message";
import FieldType from "../enum/field-type";
import FieldSystemType from "../enum/field-system-type";
import ErrorCode from "../enum/error-code";

class ApiSingleton {

    public readonly api = Client.api;

    public readonly app = Client.app;

    public readonly usr = Client.usr;

    /* Common */

    public getLocalStorage(): Storage | null {
        try {
            return (<Window>window).localStorage || null;
        } catch (e) {
            return null;
        }
    }

    public getVersion(): string {
        try {
            return Client.version();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public getObject(id: number): IApiEntity | IApiField | null {
        try {
            return this.api.getObject(id);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Application */

    public async appLoad(appId: string): Promise<IApiApplication | null> {
        try {
            const result: IApiApplication[] = await this.app.load(appId, true);
            return result[0] || null;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async appSave(app: IApiApplication): Promise<void> {
        try {
            await app.save();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async grantAccess(app: IApiApplication, options: IApiArgsGrantAccess): Promise<void> {
        try {
            await app.grantAccess(options);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async revokeAccess(app: IApiApplication, userId: number): Promise<void> {
        try {
            await app.revokeAccess(userId);
        } catch (e) {
            if (e && (e as Error).message === ServerErrorCode.RevokeOwner) {
                throw ErrorCode.UserIsOwner;
            } else {
                throw this.internalError(e as Error);
            }
        }
    }

    public getCallUrl(): ICallUser[] {
        try {
            return this.api.getCallUrl();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Entity */

    public getWebhookDico(): number[] {
        try {
            return this.api.getWebhookDico().map((i: IApiWebhhokDico) => i.c161);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public getAllEntities(): IApiEntity[] {
        try {
            return this.api.entities.AllEntities;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public getEntity(id: number): IApiEntity | null {
        try {
            return this.api.entities.getByEcn(id) || null;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public buildMasterDetailDico(): IApiMasterDetail[] {
        try {
            return this.api.buildMasterDetailDico();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async entityCreate(singular: string, fields: Partial<IField>[] = []): Promise<IApiEntity> {
        if (!fields.length) {
            fields.push({
                id: -1,
                singular,
                uiType: FieldType.text,
                uiConfig: {},
                systemType: FieldSystemType.string
            });
        }
        try {
            return await this.api.entities.buildFullEntity(singular, fields);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async entitySave(entity: IApiEntity): Promise<IApiEntity> {
        try {
            return await entity.save();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async entityDelete(entity: IApiEntity): Promise<void> {
        try {
            await entity.delete();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async entityUidCreate(entity: IApiEntity): Promise<void> {
        try {
            await entity.createUserIdField({
                singular: Msg.get(Message.owner)
            });
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Field */

    public getField(id: number): IApiField | null {
        try {
            const result: IApiEntity | IApiField | null = this.getObject(id);
            if (result && (result as IApiField).uiType) {
                return result as IApiField;
            }
            return null;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async fieldCreate(entity: IApiEntity, singular: string, uiType: FieldType = FieldType.text): Promise<IApiField> {
        try {
            return (await entity.createField({
                systemType: FieldSystemType.string,
                description: singular,
                singular,
                uiType
            }))[0];
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async fieldSave(field: IApiField): Promise<IApiField> {
        try {
            return await field.save();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async fieldDelete(field: IApiField): Promise<IApiField> {
        try {
            return await field.delete();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async relationRename(id: number, verb: string): Promise<IApiField> {
        const field: IApiField | null = this.getField(id);
        if (field && field.pointer) {
            field.pointer[0].ptVerb = verb;
            return await this.fieldSave(field);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Field Promotion */

    public async fieldCreateLinked(entity: IApiEntity, fieldId: number, config: IApiLinkedFieldConfig): Promise<IApiField> {
        try {
            const result: IApiField[] = await entity.createLinkedField(config, fieldId);
            return result[0] as IApiField;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async fieldCreateString(entity: IApiEntity, fieldId: number, singular: string, uiType: FieldType = FieldType.text): Promise<IApiField> {
        try {
            return (await entity.createStringField({
                singular,
                uiType
            }, fieldId))[0];
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async fieldChangeToLinked(field: IApiField, entityId: number, config: ILinkedFieldClientConfig): Promise<IApiField> {
        try {
            const result: IApiChangeToLinked = (await field.changeToLinkedField(config.pointerId, entityId, config.fieldId, config.verbTo, config.verbFrom, config.singular))[0];
            return this.getField(result.newLinkedFieldCN) as IApiField;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async fieldChangeToString(field: IApiField, uiType: FieldType): Promise<IApiField> {
        try {
            const result: IApiResponseFieldToString[] = await field.changeToStringField(uiType);
            return this.getField(result[0].newStringFieldCN) as IApiField;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Parameter Factory */

    public getAllParams(): IApiParamEntity[] {
        try {
            return this.api.parameterFactory.allParam;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public getParameterFactory(id: string): IApiParamFactory | null {
        try {
            return this.api.parameterFactory.get(id);
        } catch (e) {
            if (e && (e as Error).message === ServerErrorCode.UnknownParameter) {
                return null;
            }
            throw this.internalError(e as Error);
        }
    }

    public async createParameterFactory(id: string, option: number): Promise<void> {
        try {
            await this.api.parameterFactory.createNewParameter(id, option);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async deleteParameterFactory(id: string): Promise<void> {
        try {
            await this.api.parameterFactory.deleteParameter(id);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Parameter Storage */

    public async paramInsert(factory: IApiParamFactory, item: IParamItem): Promise<IParamItem> {
        try {
            return await factory.insert(item);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async paramUpdate(factory: IApiParamFactory, id: number): Promise<IParamItem> {
        try {
            return await factory.update(id);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async paramDelete(factory: IApiParamFactory, id: number): Promise<number> {
        try {
            await factory.delete(id);
        } catch (e) {
            if (e && (e as Error).message !== ServerErrorCode.NotExisting) {
                throw this.internalError(e as Error);
            }
        }
        return id;
    }

    public async paramLoad(factory: IApiParamFactory, id: number): Promise<void> {
        try {
            await factory.loadOnDemandValue(id);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async paramLoadValues(factory: IApiParamFactory): Promise<void> {
        try {
            await factory.loadValues();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Rollup */

    public async rollupCreate(rollup: Partial<IApiRollup>): Promise<IApiRollup> {
        try {
            const master: IApiEntity = this.getObject(0) as IApiEntity;
            const result: IApiRollup[] = await master.createRollupField(rollup);
            if (result.length) {
                return result[0];
            } else {
                throw this.internalError(new Error('.createRollupField returns empty array'));
            }
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public rollupGet(id: number): IApiRollup | null {
        const field: IApiField | null = this.getField(id);
        if (field && field.systemType === FieldSystemType.rollup) {
            return field as IApiRollup;
        }
        return null;
    }

    public async rollupGetData(rollup: IApiRollup): Promise<IApiRollupData[]> {
        try {
            return await rollup.getData();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Authorization */

    public async loadInstancesList(): Promise<string[]> {
        let url: string = document.location.host;
        if (url.indexOf('192.168') > -1) {
            url = 'localhost';
        }
        try {
            return (await this.api.loadInstancesList(url)) || [];
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async login(data: { user: string, password: string, instanceName: string }): Promise<IApiLoginApplication[]> {
        try {
            // TODO - Remove this - Instances hack start
            if (data.instanceName === 'main') {
                await this.api.loadInstancesList('framelink.co');
            } else if (data.instanceName === 'preprod') {
                await this.api.loadInstancesList('frametest.eu');
            } else {
                await this.loadInstancesList();
            }
            // TODO - Remove this - Instances hack end
            return await this.api.login(data);
        } catch (e) {
            if (e && (e as Error).message === ServerErrorCode.AccessDenied) {
                throw ErrorCode.AccessDenied;
            } else {
                throw this.internalError(e as Error);
            }
        }
    }

    public async use(applicationId: string): Promise<IApiLoginData> {
        try {
            return await this.api.use(applicationId);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async createUserApplication(name: string, appId: string, password: string, instance: string): Promise<string> {
        try {
            const result: IApiResponseCreateApp = await this.api.createUserApplication(name, appId, password, instance);
            return result.application.name;
        } catch (e) {
            if (e && (e as Error).message === ServerErrorCode.UserExists) {
                throw ErrorCode.UserExists;
            }
            throw this.internalError(e as Error);
        }
    }

    public async logout(): Promise<void> {
        try {
            return await this.api.logout();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Documents */

    public async getOneDocument(entityId: number, recordId: number): Promise<IDocument[]> {
        try {
            return await this.api.getOneDocument(entityId, recordId);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async insertDocuments(entityId: number, docs: IDocument[]): Promise<IDocument[]> {
        try {
            return await this.api.insertDocuments(entityId, docs.map((d: IDocument) => escapePostgre(d)));
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async replaceDocument(entityId: number, doc: IDocument): Promise<IDocument[]> {
        try {
            return await this.api.replaceDocument(entityId, doc);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async deleteMultipleDocuments(entityId: number, ids: number[]): Promise<IDocument[]> {
        const query: string = `q=id in (${ids.join(', ')})`;
        try {
            return await this.api.deleteMultipleDocuments(entityId, query);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async getAllDocuments(entityId: number, query: string): Promise<IDocument[]> {
        try {
            return await this.api.getAllDocuments(entityId, query);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Notification */

    public addNotificationHandler(callback: Function, notifType: number): void {
        const name: string = Global.DEFAULT_NOTIFICATIN_LISTENER;
        try {
            this.api.addNotificationHandler(name, callback, notifType);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* Public Api */

    public getPublicApiUrl(): string {
        try {
            return this.api.getPublicApiUrl();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* User */

    public getUID(): number {
        try {
            return this.api.getUID();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public getUserLogo(): string {
        try {
            return this.api.getUserLogo() || '';
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async userLoad(userId: string): Promise<IApiUserLoaded | null> {
        try {
            const result: IApiUserLoaded[] = await this.usr.load(userId, true);
            return result[0] || null;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async userSave(usr: IApiUserLoaded): Promise<IApiUserLoaded> {
        try {
            return await usr.save();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async createUser(name: string, password: string = Global.DEFAULT_PASSWORD): Promise<string> {
        try {
            await (new this.usr({
                type: 'user',
                password,
                name
            })).create();
            return name;
        } catch (e) {
            if ((e as Error).message) {
                if ((e as Error).message.includes(ServerErrorCode.UserNameExists)) {
                    throw ErrorCode.UserExists;
                } else if ((e as Error).message.includes(ServerErrorCode.DuplicateValue)) {
                    throw ErrorCode.UserExists;
                }
            }
            throw this.internalError(e as Error);
        }
    }

    /* Email and Sms Services */

    public async svcExec(service: ServiceName, api: ServiceApiName, groupKey: IServiceGroupKey | null, data: IServiceEmail | IServiceSms): Promise<void> {
        try {
            // TODO - Nico should update code to exclude extra .reloadAll call
            await this.api._groupKeys.reloadAll();
            return await this.api.svcExec(service, api, groupKey, data);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* WebHook Services */

    public groupKeyGetByPath(pathId: number[], service: ServiceName): IApiGroupKey[] {
        try {
            return this.api._groupKeys.getAllPerFcn(service, pathId);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public groupKeyGet(id: number): IApiGroupKey | null {
        try {
            return this.api._groupKeys.getOne(id) || null;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async groupKeyRename(id: number, action: string): Promise<void> {
        try {
            // TODO - Nico should implement specific method to rename action
            await this.api.updateOneDocument(100, id, `set=c101="${action}"`);
            // TODO - Nico should update code to exclude extra .reloadAll call
            await this.api._groupKeys.reloadAll();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async groupKeyDelete(service: ServiceName, id: number): Promise<void> {
        try {
            await this.api.deleteGroupKeys(service, [id]);
            // TODO - Nico should update code to exclude extra .reloadAll call
            await this.api._groupKeys.reloadAll();
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async getGroupKeysStatus(service: ServiceName, id: number): Promise<IApiWebhookStatus[]> {
        try {
            return await this.api.getWHFiltered(service, [], [id]);
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async getWebhooksInfo(service: ServiceName, ids: number[]): Promise<IApiWebhookInfo[]> {
        try {
            const result: IApiWebhookInfo[] = [];
            const response: IApiWebhookInfo[] = await this.api.getWHInfos(service, ids);
            response.forEach((i: IApiWebhookInfo) => {
                const existing: IApiWebhookInfo | null = result.find((r: IApiWebhookInfo) => {
                    return r.id === i.id;
                }) || null;
                if (existing) {
                    existing.histo = [...existing.histo, ...i.histo];
                } else {
                    result.push(i);
                }
            });
            return result;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    /* File System */

    public getFileUrl(filename: string): string {
        return this.api.getFileUrl(filename);
    }

    public async sendFile(filename: string, base64: string): Promise<string> {
        try {
            await this.api.sendFile_b64(filename, base64);
            return filename;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async sendFileBuffer(filename: string, buffer: ArrayBuffer): Promise<string> {
        try {
            await this.api.sendFile(filename, arrayBufferToBase64(buffer));
            return filename;
        } catch (e) {
            throw this.internalError(e as Error);
        }
    }

    public async getFile(filename: string): Promise<ArrayBuffer | null> {
        try {
            const response: IApiFileResponse = await this.api.getFile(filename);
            return base64ToArrayBuffer(response.fileContent);
        } catch (e) {
            return null;
        }
    }

    public async deleteFile(filename: string): Promise<string> {
        try {
            if (filename.length < Global.MAX_FILENAME_LENGTH) {
                await this.api.deleteFile(filename);
            }
            return filename;
        } catch (e) {
            if (e === ServerErrorCode.NoFile) {
                return filename;
            } else {
                throw this.internalError(e as Error);
            }
        }
    }

    /* Private Methods */

    private internalError(e: Error): ErrorCode {
        log('flClient throws an error', 'red');
        log(e);
        return ErrorCode.ApiInternal;
    }

}

const Api = new ApiSingleton();

export default Api;